import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Camera } from "lucide-react";
import { mdcpPointIcon } from "@/features/power_projects/components/mdcpMap";
import type { MdcpScope } from "@/features/rfq/api/proceedings";

/** Mapa zakresu MDCP w ogłoszeniu — dla oferenta (i podglądu GK „widok oferenta").
 *
 *  Geodeta wycenia dojazd i zdjęcia, więc ma ZOBACZYĆ zakres na podkładzie OSM, a nie
 *  zgadywać go z załączonego DXF-a: te same numerowane punkty ze strzałką azymutu, co po
 *  stronie GK, plus pomarańczowy obrys buforu. Dane są zamrożone w chwili publikacji —
 *  mapa pokazuje dokładnie to, co potem trafi do zlecenia zwycięzcy.
 *
 *  Pusta lista punktów to wariant PUBLICZNY (strona bez logowania): sam obrys, żeby firma
 *  z ulicy widziała gdzie i jak duża jest robota. Numerowane punkty zdjęciowe zostają za
 *  logowaniem, bo to już zakres roboty do wykonania. */
export function ProceedingMdcpScopeMap({ scope }: { scope: MdcpScope }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52, 19],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);

    const bounds: L.LatLngBounds[] = [];
    if (scope.buffer) {
      const buf = L.geoJSON(scope.buffer as never, {
        style: { color: "#ea580c", weight: 2, fillColor: "#f97316", fillOpacity: 0.1 },
      });
      buf.bindTooltip("Obrys zakresu MDCP", { sticky: true });
      buf.addTo(map);
      const b = buf.getBounds();
      if (b.isValid()) bounds.push(b);
    }
    const latlngs: L.LatLngExpression[] = [];
    for (const p of scope.points) {
      if (p.lat == null || p.lng == null) continue;
      latlngs.push([p.lat, p.lng]);
      L.marker([p.lat, p.lng], {
        icon: mdcpPointIcon({ number: p.number, azimuth_deg: p.azimuth_deg, photos_count: 0 }, false),
        zIndexOffset: 1000,
      })
        .bindTooltip(`Punkt #${p.number} · ${p.azimuth_deg}°${p.note ? ` · ${p.note}` : ""}`)
        .addTo(map);
    }
    if (latlngs.length > 0) bounds.push(L.latLngBounds(latlngs));
    if (bounds.length > 0) {
      const all = bounds.reduce((acc, b) => acc.extend(b), L.latLngBounds(bounds[0].getSouthWest(), bounds[0].getNorthEast()));
      map.fitBounds(all, { padding: [40, 40], maxZoom: 17 });
    }

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [scope]);

  const hasPoints = scope.points.length > 0;

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
        <Camera size={14} className="mr-1 inline" />
        {hasPoints
          ? `Zakres prac — mapa (${scope.points.length} ${
              scope.points.length === 1 ? "punkt zdjęciowy" : "punktów zdjęciowych"
            })`
          : "Zakres opracowania — mapa"}
      </h2>
      <p className="mb-2 text-[11px] text-[var(--color-text-muted)]">
        {hasPoints ? (
          <>
            Strzałka pokazuje kierunek zdjęcia (azymut od północy). Pomarańczowy obrys to
            zakres opracowania mapy — ten sam, co w załączonym pliku DXF.
          </>
        ) : (
          <>
            Pomarańczowy obrys to zakres opracowania mapy. Numerowane punkty zdjęciowe i plik
            DXF obrysu są widoczne dla firm dopuszczonych do postępowania — po zalogowaniu.
          </>
        )}
      </p>
      <div className="relative isolate z-0">
        <div
          ref={containerRef}
          className="w-full border border-[var(--color-border)]"
          style={{ background: "#fff", height: "380px" }}
        />
      </div>
    </section>
  );
}
